<template lang="pug">
    .form(v-if="isAccessToForm")
        .form__container
            .form__title {{ editPageTitle }}
            form.form__body(action="javascript://" @submit.prevent="submitForm" v-if="!isLoading")
                .form__field
                    .form__label {{ $i18n.t('billing.billing_stocks_price') + " (рубл.)" }}
                    .form__field-body
                        cs-input.form__input.input-number.input-number--small(
                            type="number" 
                            min="0"
                            v-model.number="formTariff.price"
                            name="price"
                        )
                divider

                .form__field
                    .form__label {{ $i18n.t('billing.billing_subscriptions_amountOfTeachers') }}
                    .form__field-body
                        cs-input.form__input.input-number.input-number--small(
                            type="number"
                            min="0" 
                            step="1"
                            v-model.number="formTariff.restriction.teachersLimit"
                            name="teachersLimit"
                        )

                .form__field
                    .form__label {{ $i18n.t('billing.billing_subscriptions_amountOfStudents') }}
                    .form__field-body
                        cs-input.form__input.input-number.input-number--small(
                            type="number"
                            min="0" 
                            step="1"
                            v-model.number="formTariff.restriction.studentsLimit"
                            name="studentsLimit"
                        )

                .form__field
                    .form__label {{ $i18n.t('progress.progress_preview_homeworkLimit') }}
                    .form__field-body
                        cs-input.form__input.input-number.input-number--small(
                            type="number"
                            min="0"
                            step="1"
                            v-model.number="formTariff.restriction.homeworkLimit"
                            name="homeworkLimit"
                            @input="markNullableDirty('homeworkLimit')"
                        )

                .form__field
                    .form__label {{ $i18n.t('progress.progress_preview_gameLimit') }}
                    .form__field-body
                        cs-input.form__input.input-number.input-number--small(
                            type="number"
                            min="0"
                            step="1"
                            v-model.number="formTariff.restriction.timeRestriction"
                            name="timeRestriction"
                            @input="markNullableDirty('timeRestriction')"
                        )

                .form__field
                    .form__label {{ $i18n.t('billing.billing_subscriptions_amountOfCourses') }}
                    .form__field-body
                        cs-input.form__input.input-number.input-number--small(
                            type="number"
                            min="0"
                            step="1"
                            v-model.number="formTariff.restriction.courses"
                            name="courses"
                        )
                divider

                .form__buttons
                    cs-button.form__button(color="blue" type="submit" :loading="isLoading" :disabled="isLoading")
                        | {{ $i18n.t('homework.homework_create_save') }}
                    cs-button.form__button(color="blue" pattern="primary-outline" @click.prevent="$router.back()")
                        | {{ $i18n.t('common.common_button_cancel') }}
            cs-loader(v-else)
    div(v-else)
        .content-box.list
            .box-body
                .column.is-12-mobile.list-item.field-column
                    | {{ $i18n.t('errors.page_not_access') }}
</template>

<script>
    import { mapState } from 'vuex';
    import TariffsApi from '@/api/tariffs/tariffs';
    import CsInput from '@/elements/CsInput.vue';
    import HelpText from '@/elements/HelpText.vue';
    import Divider from '@/elements/Divider.vue';
    import CsButton from '@/elements/CsButton.vue';
    import CsLoader from '@/elements/CsLoader.vue';
    import CsCheckbox from '@/elements/CsCheckbox.vue';
    import { extractTariffsFromResponse } from '@/utils/tariffHelper';

    export default {
        name: 'TariffEditForm',
        components: { CsCheckbox, CsLoader, CsButton, Divider, HelpText, CsInput },
        data() {
            return {
                isLoading: false,
                formTariff: this.createEmptyFormTariff(),
                originalTariff: null,
                nullableDirtyFlags: { homeworkLimit: false, timeRestriction: false },
            };
        },
        computed: {
            ...mapState({ user: state => state.auth.user }),
            isAccessToForm() {
                return this.user.isSuperadmin || this.user.isManagerOrSupport;
            },
            tariffId() {
                const rawId = this.$route?.params?.id;
                return rawId != null ? Number(rawId) : null;
            },
            editPageTitle() {
                return (
                    this.$i18n.t('list.list_tariffs_updateTitle') +
                    `- ` +
                    this.$i18n.t(`billing.billing_tariff_${this.formTariff.name}`)
                );
            },
        },
        mounted() {
            this.fetchTariffAndFillForm();
        },
        methods: {
            createEmptyFormTariff() {
                return {
                    id: null,
                    name: '',
                    price: 0,
                    restriction: {
                        id: null,
                        teachersLimit: 0,
                        studentsLimit: 0,
                        bonusGamesAccess: false,
                        homeworkLimit: 0,
                        timeRestriction: 0,
                        courses: 0,
                    },
                };
            },

            // --- fetch ---
            async fetchTariffAndFillForm() {
                if (!this.tariffId) {
                    this.$toasted.error(this.$i18n.t('errors.error_has_occurred'));
                    return;
                }
                this.isLoading = true;
                try {
                    const response = await TariffsApi.list();
                    const tariffs = extractTariffsFromResponse(response);
                    const found = tariffs.find(item => Number(item.id) === this.tariffId);
                    if (!found) {
                        this.$toasted.error(this.$i18n.t('errors.error_has_occurred'));
                        return;
                    }

                    this.originalTariff = JSON.parse(JSON.stringify(found));

                    this.formTariff = JSON.parse(JSON.stringify(found));
                    if (!this.formTariff.restriction) this.formTariff.restriction = {};

                    const restrictionForm = this.formTariff.restriction;
                    const restrictionOriginal = this.originalTariff.restriction || {};

                    restrictionForm.teachersLimit = Number(restrictionForm.teachersLimit || 0);
                    restrictionForm.studentsLimit = Number(restrictionForm.studentsLimit || 0);
                    restrictionForm.bonusGamesAccess = !!restrictionForm.bonusGamesAccess;

                    restrictionForm.homeworkLimit =
                        restrictionOriginal.homeworkLimit == null ? 0 : Number(restrictionForm.homeworkLimit);

                    restrictionForm.timeRestriction =
                        restrictionOriginal.timeRestriction == null ? 0 : Number(restrictionForm.timeRestriction);

                    restrictionForm.courses = Number(restrictionForm.courses || 0);
                    this.formTariff.price = Number(this.formTariff.price || 0);

                    this.nullableDirtyFlags = { homeworkLimit: false, timeRestriction: false };
                } catch (e) {
                    this.showErrorMessage(e);
                } finally {
                    this.isLoading = false;
                }
            },

            markNullableDirty(fieldName) {
                if (this.nullableDirtyFlags[fieldName] !== true) {
                    this.$set(this.nullableDirtyFlags, fieldName, true);
                }
            },

            buildPayloadForSubmit() {
                const current = this.formTariff;
                const original = this.originalTariff || {};
                const originalRestriction = original.restriction || {};

                const homeworkLimitToSend = this.nullableDirtyFlags.homeworkLimit
                    ? Number(current.restriction.homeworkLimit)
                    : originalRestriction.homeworkLimit ?? null;

                const timeRestrictionToSend = this.nullableDirtyFlags.timeRestriction
                    ? Number(current.restriction.timeRestriction)
                    : originalRestriction.timeRestriction ?? null;

                return {
                    name: current.name,
                    price: Number(current.price),
                    restriction: {
                        teachers_limit: Number(current.restriction.teachersLimit),
                        students_limit: Number(current.restriction.studentsLimit),
                        bonus_games_access: !!current.restriction.bonusGamesAccess,
                        homework_limit: homeworkLimitToSend,
                        time_restriction: timeRestrictionToSend,
                        courses: Number(current.restriction.courses),
                    },
                };
            },

            submitForm() {
                this.$validator.validateAll().then(async isValid => {
                    if (!isValid) return;
                    if (!this.tariffId) {
                        this.$toasted.error(this.$i18n.t('errors.error_has_occurred'));
                        return;
                    }
                    this.isLoading = true;
                    try {
                        const payload = this.buildPayloadForSubmit();
                        await TariffsApi.update(this.tariffId, payload);
                        this.$toasted.success(this.$i18n.t('list.list_saved'));
                    } catch (error) {
                        this.showErrorMessage(error);
                    } finally {
                        this.isLoading = false;
                    }
                });
            },

            showErrorMessage(errorObject) {
                const message =
                    errorObject?.response?.data?.message ||
                    errorObject?.message ||
                    this.$i18n.t('errors.unknown_error');
                this.$toasted.error(message);
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/forms/form.scss';

    .input-number {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        -moz-appearance: textfield;
    }

    .input-number--small {
        max-width: 160px;
    }
</style>
